import { createOrUpdateBorrower } from '@/apiCalls/borrower';
import { submitApplication as submitApplicationToIq } from '@/apiCalls/submitApplication';
import { DisplayButtons } from '@/components/DisplayButtons';
import {
    ELoanUsage,
    EUserFlow,
    selectLoanUsage,
    selectSelectedLender,
    setProgressPercent,
} from '@/context/borrower';
import {
    EDisplays,
    selectMandatorySupportingDocuments,
    selectPartner,
    selectUserFlow,
    setDisplay,
    setIsAssetAndLiabilitiesSubmitSkipped,
} from '@/context/display';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { useCopy } from '@/copy';
import { useFieldCopy } from '@/copy';
import { canSkipSupportingDocs } from '@/utils/canSkipSupportingDocs';
import {
    isAllCreditCardsComplete,
    isAllMotorVehicleAssetsComplete,
    isAllPersonalLoansComplete,
    isAllRealEstatesComplete,
} from '@/utils/checkAssetsAndLiabilities';
import { getRequiredDocuments } from '@/utils/getRequiredDocuments';
import { EConversionType, reportConversion } from '@/utils/reportConversion';
import { SetFinalScreen } from '@/utils/SetFinalScreen';

interface IAssetsAndLiabilitiesButtonsProps {
    setShowErrors: (value: boolean) => void;
    baseId?: string;
}

export function AssetsAndLiabilitiesButtons({
    setShowErrors,
    baseId,
}: IAssetsAndLiabilitiesButtonsProps): JSX.Element {
    // ***** Redux State *****
    const dispatch = useAppDispatch();
    const fields = useFieldCopy();
    const selectedLender = useAppSelector(selectSelectedLender);
    const loanUsage = useAppSelector(selectLoanUsage);
    const skipSupportingDocs = canSkipSupportingDocs();
    const { submitLabelWhenSubmitting } = useCopy();

    /**
     * canSkipSupportingDocs is false when there are no required documents at
     * all, but SupportingDocs submits on mount in that case — so the customer
     * pressed a button labelled Continue and the application went to the
     * lender. Treat that as submitting too.
     *
     * Not when there is no selected lender: that path goes to the referral
     * screen or collects the brand's documents, neither of which submits.
     */
    const willSubmit =
        !!selectedLender && (skipSupportingDocs || !getRequiredDocuments());
    const mandatoryDocs = useAppSelector(selectMandatorySupportingDocuments);
    const hasMandatoryDocs = Boolean(mandatoryDocs?.length);
    const userFlow = useAppSelector(selectUserFlow);
    const isNovatedLease = loanUsage === ELoanUsage.NOVATED;
    const partner = useAppSelector(selectPartner);
    const skipPreapproval = partner.skipPreapproval;

    // ***** Event Handlers *****
    function handleBackClick(): void {
        if (userFlow === EUserFlow.CAMPAIGN) {
            dispatch(setDisplay(EDisplays.OCR_SUMMARY));
            return;
        }
        if (loanUsage === ELoanUsage.BUSINESS) {
            dispatch(setDisplay(EDisplays.OCR_SUMMARY));
            return;
        }
        if (loanUsage === ELoanUsage.NOVATED) {
            if (skipPreapproval) {
                dispatch(setDisplay(EDisplays.OCR_SUMMARY));
                return;
            }
            dispatch(setDisplay(EDisplays.INDICATIVELY_APPROVED));
            return;
        }
        dispatch(setDisplay(EDisplays.EMPLOYMENT));
    }

    function handleNextClick(): void {
        setShowErrors(true);
        if (!isComplete()) return;
        createOrUpdateBorrower();
        // if (loanUsage === ELoanUsage.NOVATED) {
        //     dispatch(setIsAssetAndLiabilitiesSubmitSkipped(true));
        //     dispatch(setDisplay(EDisplays.SUPPORTING_DOCS));
        //     return;
        // }
        // No lender to submit to (no quotes, or a no-lender journey). Normally
        // that ends the journey on the referred screen — but if the brand
        // enforces its own documents we still collect those first, otherwise
        // the brand's mandatory documents could never be gathered at all.
        //
        // Deliberately not short-circuited by skipSupportingDocs: with no
        // lender the only required documents are the brand's, so skipping would
        // give this journey two different endings depending on whether the
        // customer happened to have already uploaded them.
        if (!selectedLender) {
            if (!hasMandatoryDocs) {
                dispatch(setDisplay(EDisplays.INDICATIVELY_REFERRED));
                return;
            }
            dispatch(setIsAssetAndLiabilitiesSubmitSkipped(true));
            dispatch(setDisplay(EDisplays.SUPPORTING_DOCS));
            return;
        }

        if (skipSupportingDocs) {
            submitApplication();
        } else {
            dispatch(setIsAssetAndLiabilitiesSubmitSkipped(true));
            dispatch(setDisplay(EDisplays.SUPPORTING_DOCS));
        }
    }

    // ***** Helpers *****
    function isComplete(): boolean {
        if (!isAllRealEstatesComplete(true)) return false;
        if (!isAllPersonalLoansComplete(true)) return false;
        if (!isAllMotorVehicleAssetsComplete(true)) return false;
        if (!isAllCreditCardsComplete(true)) return false;

        return true;
    }

    function submitApplication(): void {
        dispatch(setProgressPercent(100));
        submitApplicationToIq();
        reportConversion(EConversionType.APPLY_TO_LENDER);
        SetFinalScreen();
    }

    return (
        <DisplayButtons
            idLeftButton={`${baseId}-button-back`}
            idRightButton={`${baseId}-button-next`}
            rightClick={handleNextClick}
            leftClick={handleBackClick}
            rightText={
                (
                    submitLabelWhenSubmitting
                        ? willSubmit
                        : skipSupportingDocs
                )
                    ? fields.submitApplication
                    : fields.nextButton
            }
        />
    );
}
